import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { helpFor, SUGGESTION_MAX } from '@norum/contracts';
import { failureOf, said } from '@/api/errors';
import { useSuggest } from '@/api/hooks';
import { Dialog } from '@/ui/Dialog';
import { Field, HeaderButton } from '@/ui/primitives';
import { Textarea } from '@/ui/fields';
import { useToast } from '@/ui/Toast';

/**
 * The ? on every page: what the page is for and how it is used, from the
 * pages' own help (page-help.ts) — the same words an agent is given about
 * the page — with the two ways on from it: ask about the page in the side
 * chat, or suggest a change to it, which goes to Norum Tech to be written
 * up as a spec for the superadmin (0132).
 */
export function PageHelpButton({ wide, onAsk }: { wide: boolean; onAsk: (question: string) => void }) {
  const location = useLocation();
  const help = helpFor(location.pathname);
  const [open, setOpen] = useState<'help' | 'suggest' | null>(null);
  if (!help) return null;
  return (
    <>
      <HeaderButton label="What this page is for" glyph="help" word={wide && 'Help'} onClick={() => setOpen('help')} />
      {open && <HelpDialog mode={open} onMode={setOpen} onAsk={onAsk} />}
    </>
  );
}

function HelpDialog({ mode, onMode, onAsk }: { mode: 'help' | 'suggest'; onMode: (m: 'help' | 'suggest' | null) => void; onAsk: (question: string) => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const suggest = useSuggest();
  const [text, setText] = useState('');
  const help = helpFor(location.pathname)!;
  const close = () => onMode(null);

  if (mode === 'suggest') {
    const ready = text.trim().length >= 10 && text.length <= SUGGESTION_MAX;
    return (
      <Dialog
        open
        onOpenChange={(o) => (o ? undefined : close())}
        kicker={`Suggest a change · ${help.title}`}
        title="What would you change, and why?"
        secondary={{ label: 'Back', onClick: () => onMode('help') }}
        primary={{
          label: suggest.isPending ? 'Sending…' : 'Send to Norum Tech',
          disabled: !ready || suggest.isPending,
          onClick: () =>
            suggest.mutate(
              { route: location.pathname, suggestion: text.trim() },
              {
                onSuccess: (r) => {
                  close();
                  toast({
                    text: r.existing
                      ? 'You already suggested this; it is still with Norum Tech.'
                      : r.requestId
                        ? 'Sent. Norum Tech writes it up as a spec, and the superadmin decides whether it is built.'
                        : 'Sent to Norum Tech’s lead, as a task on their day.',
                    ...(r.requestId ? { link: { label: 'Follow it', to: `/requests/${r.requestId}` } } : {}),
                  });
                },
                onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
              },
            ),
        }}
        note="Norum Tech writes it up as a spec: what is asked, why, what would change, and whether it can be built by the platform itself or needs an engineer. Nothing changes until the superadmin decides."
      >
        <Field label="Your suggestion" hint="What is missing, confusing or could be better on this page — and what you would like instead.">
          <Textarea aria-label="Your suggestion" className="min-h-[140px]" value={text} onChange={(e) => setText(e.target.value)} maxLength={SUGGESTION_MAX} />
        </Field>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => (o ? undefined : close())}
      kicker="About this page"
      title={help.title}
      secondary={{ label: 'Close', onClick: close }}
      primary={{ label: 'Suggest a change', onClick: () => onMode('suggest') }}
    >
      <div className="flex flex-col gap-4" data-testid="page-help">
        <p className="m-0 text-body">{help.purpose}</p>
        <section className="flex flex-col gap-1.5" aria-label="How it is used">
          <h3 className="m-0 text-label uppercase tracking-label text-meta">How it is used</h3>
          <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-dense">
            {help.howTo.map((h) => (
              <li key={h}>{h}</li>
            ))}
          </ul>
        </section>
        {help.who && (
          <p className="m-0 text-dense text-secondary">
            <span className="text-meta">Who works on it: </span>
            {help.who}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="min-h-tap self-start text-dense underline underline-offset-2"
            onClick={() => {
              close();
              onAsk(`Explain the ${help.title} page to me: what is it for, and how do I use it here?`);
            }}
          >
            Ask about this page in the chat
          </button>
          <button type="button" className="min-h-tap self-start text-dense underline underline-offset-2" onClick={() => { close(); void navigate('/requests'); }}>
            My suggestions, under Requests
          </button>
        </div>
      </div>
    </Dialog>
  );
}
